import { usePermissions } from 'src/state/redux/selector/permissions'
import {
  FeatureFlags,
  FeatureFlagKeys,
} from 'src/types/featureFlags'

type MappedFeatureFlags = { [K in FeatureFlagKeys]: typeof FeatureFlags[K]['defaultValue'] }
function useFeatureFlags(): MappedFeatureFlags
function useFeatureFlags<T extends FeatureFlagKeys>(key: T): typeof FeatureFlags[T]['defaultValue']
function useFeatureFlags<T extends FeatureFlagKeys>(...keys: T[]): { [K in T]: typeof FeatureFlags[K]['defaultValue'] }
function useFeatureFlags<
  T extends FeatureFlagKeys,
  S extends typeof FeatureFlags[T]['defaultValue'],
  M extends { [K in T]: typeof FeatureFlags[K]['defaultValue']}
>(
  ...keys: T[]
): (
  | MappedFeatureFlags
  | S
  | M
) {
  const computedFlags = usePermissions()

  // Case 1: Return all flags
  if (!keys.length) {
    return computedFlags
  }

  // Case 2: Return single flag value
  if (keys.length === 1) {
    const alias = keys[0]
    return computedFlags[alias] as unknown as S
  }

  // Case 3: Return mapped flags + values
  const rv = keys.reduce<M>(
    (acc, alias) => ({
      ...acc,
      [alias]: computedFlags[alias],
    }),
    {} as M,
  );

  return rv;
}

export default useFeatureFlags
